import { Checkbox } from "primereact/checkbox";
import { FC, useEffect, useState } from "react";
import { useGetAllPopUpValuesQuery } from "../../data/api/CatalogueApi";
import { Header, HeaderOptions, HeaderTypes, } from "../../data/model/DataModels";
import SpinnerComponent from "../Spinner/SpinnerComponent";
import styles from "./HeaderPopUp.module.scss"
import { InputNumber } from "primereact/inputnumber";
import { extractRangeMinAndMax, rangeValidation } from "../utils/utils";


export interface HeaderPopUpProps {
	headerType: HeaderTypes;
	selectValues: HeaderOptions[];
	setHeaderOptions: Function;
	isSolution?: boolean;
}

const HeaderPopUp: FC<HeaderPopUpProps> = ({ headerType, selectValues, setHeaderOptions, isSolution = false }) => {
	const { data: allPopUpValues = [], isLoading: isLoadingallPopUpValues } = useGetAllPopUpValuesQuery(headerType);
	const [selectedOptions, setSelectedOptions] = useState(selectValues as HeaderOptions[])
	const CONTRACT_VALIDITY_CUSTOM = 'CUSTOM';

	useEffect(() => {
		if(headerType === HeaderTypes.CONTRACT_VALIDITY && selectValues.length === 1 && selectValues[0]?.code === CONTRACT_VALIDITY_CUSTOM) {
			const _customRange = extractRangeMinAndMax(selectValues[0].name);
			const updated = selectedOptions.map(opt => opt.code === CONTRACT_VALIDITY_CUSTOM ? { ...opt, min: _customRange.min, max: _customRange.max } : opt );
			setSelectedOptions(updated);
		}
	},[selectValues, headerType])

	const customRange = headerType === HeaderTypes.CONTRACT_VALIDITY ? selectedOptions.find(opt => opt.code === CONTRACT_VALIDITY_CUSTOM) : undefined;

	const customContractRangeValidation = () => {
		if(headerType === HeaderTypes.CONTRACT_VALIDITY && selectedOptions.length === 1 && selectedOptions[0].code === CONTRACT_VALIDITY_CUSTOM) {
			return rangeValidation(selectedOptions[0].min, selectedOptions[0].max);
		}
		return { 
			"minError" : '',
			"maxError" : ''
		}
	}

	const setCustomOptionName = (minValue:any, maxValue:any) => {
		const updatedCustomOptions = selectedOptions.map(option => {
				if (option.code === "CUSTOM") {
					// Remove any existing (x-y) from the name
					const cleanName = option.name.replace(/\s*\((\d+|null)-(\d+|null)\)/g, '').trim();
					// Add the new range
					return {code: option.code, name: `${cleanName} (${minValue}-${maxValue})`, min: minValue, max: maxValue};
				}
				return option;
			});
			setSelectedOptions(updatedCustomOptions);
			setHeaderOptions(updatedCustomOptions);
	}

	const handleCustomOptionRange = (key: 'min'|'max', e: any) => {
		const updatedCustomOption = selectedOptions.map(opt => opt.code === CONTRACT_VALIDITY_CUSTOM ? { ...opt, [key]: e.value } : opt );
		setSelectedOptions(updatedCustomOption);
		setHeaderOptions(updatedCustomOption);
		const customOption = updatedCustomOption.find(opt => opt.code === CONTRACT_VALIDITY_CUSTOM);
		if (!customOption) return;
  	setCustomOptionName(customOption.min, customOption.max);
	}

	const onCategoryChange = (e: any) => {
		let _selectedCategories = [...selectedOptions];
		if (e.checked) {
			//If it is a product, then we can select only 1 Service Model
			if (!isSolution && (headerType === HeaderTypes.SERVICE_MODEL)) {
				_selectedCategories = [];
			}
			if(e.value.code === CONTRACT_VALIDITY_CUSTOM){
				_selectedCategories.push({...e.value, min: null, max: null});
			}
			else
				_selectedCategories.push(e.value);
		}
		else {
			_selectedCategories = _selectedCategories.filter(category => category?.code !== e?.value?.code);
		}
		setSelectedOptions(_selectedCategories);
		setHeaderOptions(_selectedCategories);
	};

	const selectAll = () => {
		if(HeaderTypes.CONTRACT_VALIDITY===headerType){
			const nonCustomOptions : Header[] = allPopUpValues.filter((item: HeaderOptions) => item?.code !== "CUSTOM");
			setSelectedOptions(nonCustomOptions);
			setHeaderOptions(nonCustomOptions);
		}
		else {
			setSelectedOptions(allPopUpValues);
			setHeaderOptions(allPopUpValues);
		}
	}

	const deSelectAll = () => {
		setSelectedOptions([]);
		setHeaderOptions([]);
	}

	const isContractValidityCustomOptionDisabled = (option : HeaderOptions) => {
		if(HeaderTypes.CONTRACT_VALIDITY===headerType){
			return option.code==="CUSTOM" && selectedOptions.some((item: HeaderOptions) => item?.code !== option?.code)
				|| option.code!=="CUSTOM" && selectedOptions.some((item: HeaderOptions) => item?.code === "CUSTOM");
		}
		return false;
	}

	return (
		<div data-testid="HeaderPopUp">
			<div>
				{isLoadingallPopUpValues && <SpinnerComponent />}
				<div className={styles.headerBody}>
					{(!isLoadingallPopUpValues && allPopUpValues.length > 3) && <div className={styles.linkSection}>
						<span onClick={selectAll} > Select All</span>
						<span onClick={deSelectAll}> De-select All</span>
					</div>
					}
					<div className={styles.optionsSection}>
						{allPopUpValues?.map((category: HeaderOptions) => {
							return (
								<div key={category?.code}>
									<Checkbox inputId={category?.code} name="category" value={category} onChange={onCategoryChange} 
										checked={selectedOptions?.some((item: HeaderOptions) => item?.code === category?.code)} 
										disabled={isContractValidityCustomOptionDisabled(category)}/>
									<label htmlFor={category?.code} 
									className={`${styles.optionLabel} ${isContractValidityCustomOptionDisabled(category) ? styles['disabled-label'] : ''}`}>
										{category?.name}
									</label>
								</div>
							);
						})}
						{ customRange &&
							<div className={styles.customRangeRow}>
								<div className={styles.rangeSection}>
									<div>Min</div>
									<InputNumber value={customRange.min} size={20} useGrouping={false} onValueChange={(e) => handleCustomOptionRange('min', e)}/>
									{ customContractRangeValidation().minError !== "" && 
										<div className={styles.invalid}>{customContractRangeValidation().minError}</div> 
									}
								</div>
								<div className={styles.rangeSection}>
									<div>Max</div>
									<InputNumber value={customRange.max} size={20} useGrouping={false} onValueChange={(e) => handleCustomOptionRange('max', e)}/>
									{ customContractRangeValidation().maxError !== "" &&
										<div className={styles.invalid}>{customContractRangeValidation().maxError}</div> 
									}
								</div>
							</div>
						}
					</div>
				</div>
			</div>
		</div>
	)

}

export default HeaderPopUp;
